import { Formik, FormikHelpers, Form } from "formik";
import { useCallback, useMemo } from "react";
import * as Yup from "yup";
import { PhoneInput } from "@/core/components/phone-input";
import { SignupSourceSelector } from "@/core/components/signup-source-selector";
import { Button } from "@volca/ui/components/ui/button";
import { FormikCheckboxWithLabel } from "@volca/ui/components/ui/checkbox-with-label";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { SIDEBAR_MODES } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { formatDate } from "@/core/utils/date";
import { normalizeSignupSource } from "@/core/utils/form";
import { formatPhoneNumber } from "@/core/utils/phone";
import { CUSTOMER_STATUS } from "@/customer/constants";
import { CustomerWithReferralsAndPayouts } from "@/customer/customer";
import { useUpsertCustomer } from "@/customer/query";

interface CustomerFormValues {
  name: string;
  phone_number: string;
  signup_source_id: string;
  signup_source_type: string;
  onboard: boolean;
}

const validationSchema = Yup.object().shape({
  name: Yup.string(),
  phone_number: Yup.string().required("Phone number is required"),
  signup_source_id: Yup.string(),
  signup_source_type: Yup.string(),
});

interface DetailsTabProps {
  businessId: string;
  initialData: CustomerWithReferralsAndPayouts | null;
  isNewMode: boolean;
  canChangeAttribution: boolean;
}

export const DetailsTab = ({
  businessId,
  initialData,
  isNewMode,
  canChangeAttribution,
}: DetailsTabProps) => {
  const { updateSidebar } = useBusinessSettingsSidebar();
  const { mutate: upsertCustomer } = useUpsertCustomer(businessId);

  const initialValues = useMemo<CustomerFormValues>(
    () => ({
      name: initialData?.name || "",
      phone_number: formatPhoneNumber(initialData?.phone_number) || "",
      signup_source_id: initialData?.signup_source?.id || "none",
      signup_source_type: initialData?.signup_source?.class_name || "",
      onboard: false,
    }),
    [initialData],
  );

  const handleSubmit = useCallback(
    async (
      values: CustomerFormValues,
      { resetForm }: FormikHelpers<CustomerFormValues>,
    ) => {
      // Ensure both signup source fields are set or neither is set
      const normalizedSignupSource = normalizeSignupSource(
        values.signup_source_id,
        values.signup_source_type,
      );

      return upsertCustomer(
        {
          ...values,
          id: initialData?.id,
          ...normalizedSignupSource,
          status: CUSTOMER_STATUS.ACTIVE,
          onboard: isNewMode && values.onboard,
        },
        {
          onSuccess: (updatedCustomer) => {
            resetForm({ values });

            if (isNewMode) {
              updateSidebar({
                selectedItemId: updatedCustomer.id,
                mode: SIDEBAR_MODES.view,
              });
            } else {
              updateSidebar({
                selectedItemId: updatedCustomer.id,
              });
            }
            toast.success(
              `Customer ${isNewMode ? "created" : "updated"} successfully`,
            );
          },
          onError: (error) => {
            toast.error(
              `Failed to ${isNewMode ? "create" : "update"} advocate`,
              {
                description:
                  error instanceof Error ? error.message : "Please try again",
              },
            );
          },
        },
      );
    },
    [initialData, isNewMode, updateSidebar, upsertCustomer],
  );

  return (
    <div className="h-full overflow-y-auto pr-2">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, dirty, resetForm }) => (
          <Form className="w-full pb-6">
            <Stack className="w-full" align="start" gap={6}>
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter name"
                value={values.name}
                onChange={(e) => setFieldValue("name", e.target.value)}
                fullWidth
              />

              <PhoneInput
                name="phone_number"
                value={values.phone_number}
                onChange={(value) => setFieldValue("phone_number", value)}
                disabled={!isNewMode}
                required
                fullWidth
                disabledTooltip={
                  <>
                    {" "}
                    Contact support at{" "}
                    <span className="font-semibold">support@volca.ai</span> to
                    change a customer phone number
                  </>
                }
              />

              {isNewMode && (
                <FormikCheckboxWithLabel
                  id="onboard"
                  label="Send advocate program sign-up SMS"
                  checked={values.onboard}
                  onCheckedChange={(checked) =>
                    setFieldValue("onboard", !!checked)
                  }
                  containerClassName="pl-px"
                />
              )}

              <SignupSourceSelector
                id={`signup-source-details-${initialData?.id}`}
                businessId={businessId}
                selectedSourceId={values.signup_source_id}
                selectedSourceType={values.signup_source_type}
                onSourceChange={(sourceId, sourceType) => {
                  void setFieldValue("signup_source_id", sourceId);
                  void setFieldValue("signup_source_type", sourceType);
                }}
                disabled={!canChangeAttribution}
                disabledTooltip={
                  !canChangeAttribution
                    ? "Only managers can change customer attribution"
                    : undefined
                }
              />

              {initialData && !isNewMode && (
                <>
                  <Stack className="w-full" align="start" gap={1}>
                    <Text variant="label" tone="muted">
                      Created At
                    </Text>
                    <Text>{formatDate(initialData.created_at)}</Text>
                  </Stack>

                  {initialData.email && (
                    <Stack className="w-full" align="start" gap={1}>
                      <Text variant="label" tone="muted">
                        Email
                      </Text>
                      <Text>{initialData.email}</Text>
                    </Stack>
                  )}
                </>
              )}

              <Stack align="start" gap={4} direction="row" className="mt-4">
                <Button type="submit" size="sm" disabled={!dirty}>
                  {isNewMode ? "Create customer" : "Save changes"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={!dirty}
                  onClick={() => resetForm()}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Form>
        )}
      </Formik>
    </div>
  );
};
